/**
 * API axios instance.
 * Use this for all api requests.
 */

import axios, { type AxiosError, type AxiosRequestConfig } from 'axios';
import { useTokenInterceptors } from '@/axios/interceptors';
import { API_URL } from '@/utils/helpers';

const apiInstance = axios.create({
  baseURL: API_URL,
});

const handleError = <T>(error: AxiosError, defaultData?: T) => {
  return { data: (error.response?.data as T) ?? defaultData ?? ({} as T), response: error.response, error };
};

useTokenInterceptors(apiInstance);

/**
 * Create an object with enhanced Axios methods.
 */
const api = {
  async get<T>(url: string, config?: AxiosRequestConfig, defaultData?: T) {
    try {
      const response = await apiInstance.get<T>(url, config);

      return { data: response.data, response, error: null };
    } catch (e) {
      const error = e as AxiosError;

      return handleError<T>(error, defaultData);
    }
  },
  async delete<T>(url: string, config?: AxiosRequestConfig, defaultData?: T) {
    try {
      const response = await apiInstance.delete<T>(url, config);

      return { data: response.data, response, error: null };
    } catch (e) {
      const error = e as AxiosError;

      return handleError<T>(error, defaultData);
    }
  },
  async post<T>(url: string, data?: AxiosRequestConfig['data'], config?: AxiosRequestConfig, defaultData?: T) {
    try {
      const response = await apiInstance.post<T>(url, data, config);

      return { data: response.data, response, error: null };
    } catch (e) {
      const error = e as AxiosError;

      return handleError<T>(error, defaultData);
    }
  },
  async put<T>(url: string, data?: AxiosRequestConfig['data'], config?: AxiosRequestConfig, defaultData?: T) {
    try {
      const response = await apiInstance.put<T>(url, data, config);

      return { data: response.data, response, error: null };
    } catch (e) {
      const error = e as AxiosError;

      return handleError<T>(error, defaultData);
    }
  },
  async patch<T>(url: string, data?: AxiosRequestConfig['data'], config?: AxiosRequestConfig, defaultData?: T) {
    try {
      const response = await apiInstance.patch<T>(url, data, config);

      return { data: response.data, response, error: null };
    } catch (e) {
      const error = e as AxiosError;

      return handleError<T>(error, defaultData);
    }
  },
};

export { api };
